import { ActionBar, CommonPage, Content, InRow, LoadableContent, OpenablePanel, PageTitle, ResponsiveGap } from "common/Grid";
import { TableView } from "./TableView";
import { useGetStaffUsersListQuery } from "store/action/staffUserApi";
import { PrimaryButton, SecondaryButton } from "common/Forms";
import { useNavigate, createSearchParams, useSearchParams } from "react-router-dom";
import { WithPagination } from "common/Pagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STAFF_ROLES } from "Constant/Constant";
import { useGetSiteIdOptionsMutation } from "store/action/options-store";
import { GrClose } from "react-icons/gr";
import { AdvancedSearchForm } from "./AdvancedSearchForm";


const List = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [getSiteIdOptionsApi] = useGetSiteIdOptionsMutation();
  const [siteIdOptions, setSiteIdOptions] = useState([]);

  const searchFields = useMemo(
    () => ({
      page: { name: "p", default: 1 },
      limit: { name: "ps", default: 10 },
      search: { name: "search", default: "" },
      role: { name: "role", default: "" },
      isActive: { name: "isActive", default: "" },
      siteid: { name: "siteid", default: "" },
    }),
    [],
  );

  const getSearchValue = useCallback(
    (name, defaultValue, getter = (value) => value) => {
      const value = searchParams.get(name);
      if (value === null) return defaultValue;
      return getter(value);
    },
    [searchParams],
  );

  const page = useRef(
    getSearchValue(
      searchFields.page.name,
      searchFields.page.default,
      (x) => parseInt(x) ?? searchFields.page.default,
    ),
  );
  const limit = useRef(
    getSearchValue(
      searchFields.limit.name,
      searchFields.limit.default,
      (x) => parseInt(x) ?? searchFields.limit.default,
    ),
  );

  const [filters, setFilters] = useState({
    search: getSearchValue(searchFields.search.name, ""),
    role: getSearchValue(searchFields.role.name, ""),
    isActive: getSearchValue(searchFields.isActive.name, ""),
    siteid: getSearchValue(searchFields.siteid.name, ""),
  });

  const applySearch = useCallback(
    (overrideFilters) => {
      const activeFilters = overrideFilters ?? filters;
      const updatedSearchParams = createSearchParams(searchParams);
      updatedSearchParams.set(searchFields.page.name, `${page.current}`);
      updatedSearchParams.set(searchFields.limit.name, `${limit.current}`);

      if (activeFilters.search)
        updatedSearchParams.set(searchFields.search.name, activeFilters.search);
      else updatedSearchParams.delete(searchFields.search.name);

      if (activeFilters.role)
        updatedSearchParams.set(searchFields.role.name, activeFilters.role);
      else updatedSearchParams.delete(searchFields.role.name);

      if (activeFilters.isActive !== "")
        updatedSearchParams.set(searchFields.isActive.name, activeFilters.isActive);
      else updatedSearchParams.delete(searchFields.isActive.name);

      if (activeFilters.siteid)
        updatedSearchParams.set(searchFields.siteid.name, activeFilters.siteid);
      else updatedSearchParams.delete(searchFields.siteid.name);

      setSearchParams(updatedSearchParams);
    },
    [filters, searchFields, searchParams, setSearchParams],
  );

  const handleApplyFilters = () => {
    page.current = 1;
    applySearch();
  };

  const handleClearFilters = () => {
    const cleared = { search: "", role: "", isActive: "", siteid: "" };
    setFilters(cleared);
    page.current = 1;
    applySearch(cleared);
  };

  const onPageHandler = useCallback(
    (pageRequest) => {
      page.current = pageRequest + 1;
      applySearch();
    },
    [applySearch],
  );

  const onPageSizeHandler = useCallback(
    (sizeRequest) => {
      limit.current = sizeRequest;
      page.current = 1;
      applySearch();
    },
    [applySearch],
  );

  useEffect(() => {
    applySearch();
  }, []);

  useEffect(() => {
    getSiteIdOptionsApi().then((response) => {
      setSiteIdOptions(response?.data?.data ?? []);
    });
  }, [getSiteIdOptionsApi]);

  const [sorting, setSorting] = useState({ sortBy: null, sortOrder: null });

  const onSortChange = useCallback((sortBy, sortOrder) => {
    setSorting(prev =>
      prev.sortBy === sortBy && prev.sortOrder === sortOrder
        ? { sortBy: null, sortOrder: null }
        : { sortBy, sortOrder }
    );
  }, []);

  const queryArgs = {
    page: getSearchValue(searchFields.page.name, searchFields.page.default, (x) => parseInt(x)),
    limit: getSearchValue(searchFields.limit.name, searchFields.limit.default, (x) => parseInt(x)),
    search: getSearchValue(searchFields.search.name, ""),
    role: getSearchValue(searchFields.role.name, "") || [STAFF_ROLES.STAFF_USER, STAFF_ROLES.STAFF_ADMIN],
    isActive: getSearchValue(searchFields.isActive.name, ""),
    siteid: getSearchValue(searchFields.siteid.name, ""),
    ...(sorting.sortBy !== null && { sortBy: sorting.sortBy, sortOrder: sorting.sortOrder }),
  };

  const {
    data: response,
    isLoading,
    isError,
  } = useGetStaffUsersListQuery(queryArgs, { refetchOnMountOrArgChange: true });

  const allUsers = response?.data || [];

  return (
    <CommonPage>
      <Content>
        <PageTitle title="Staff Users">
          <PrimaryButton
            action={() => navigate("/create-staff-user")}
            classNames={["text-[16px]"]}>
            Create Staff User
          </PrimaryButton>
        </PageTitle>

        <ResponsiveGap left={false} right={false}>
          <OpenablePanel
            isOpenedByDefault={false}
            openControlRenderer={(action) => (
              <ActionBar classNames={["justify-end"]}>
                <PrimaryButton action={action}>Advanced Search</PrimaryButton>
              </ActionBar>
            )}
            closeControlRenderer={(action) => (
              <ResponsiveGap top={false} left={false} right={false}>
                <ActionBar classNames={["justify-end"]}>
                  <InRow>
                    <SecondaryButton action={action}>
                      <GrClose size={20} />
                    </SecondaryButton>
                  </InRow>
                </ActionBar>
              </ResponsiveGap>
            )}>
            <AdvancedSearchForm
              filters={filters}
              setFilters={setFilters}
              siteIdOptions={siteIdOptions}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />
          </OpenablePanel>
        </ResponsiveGap>

        <LoadableContent isLoading={isLoading} isError={isError}>
          <WithPagination
            id="pagedData"
            currentPage={(response?.currentPage ?? 1) - 1}
            totalPages={response?.totalPages ?? 0}
            totalItems={response?.total ?? 0}
            pageSize={limit.current ?? 0}
            onPageChange={onPageHandler}
            onPageSizeChange={onPageSizeHandler}
            paginationWrapper={(x) => <ResponsiveGap>{x}</ResponsiveGap>}>
            <TableView staffUserList={allUsers} sorting={sorting} onSortChange={onSortChange} />
          </WithPagination>
        </LoadableContent>
      </Content>
    </CommonPage>
  );
};

export default List;
